import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Divider,
    Flex,
    Heading,
    Image,
    Spinner,
    Text,
    VStack,
    useDisclosure, SimpleGrid,
} from "@chakra-ui/react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import RegistrationModal from "./RegistrationModal";
import SuccessModal from "./SuccessModal";
import img6 from "../../assets/images/img-6.png";
import EventsService from '../../api/services/EventsService';
import { BASE_URL } from "../../config";
import OverlapModal from "./OverlapModal";
import NoCover from "../../components/NoCover/NoCover";
import ImageModal from "./ImageModal.jsx";
import TicketsCart from "../../components/tickets/TicketsCart";
import Event from "../../components/events/Event";
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MapClickHandler from "../../components/map/MapClickHandler";
import { useTranslation } from "react-i18next";
import EventDetailHeaderPage from "./EventDetailHeaderPage";
import BuyTicketButton, { isCancelled } from "./BuyTicketButton";
import useFavorites from '../AllEvents/useFavorites';
import FreeTicketsQuantity from "../../components/tickets/FreeTicketsQuantity";
import { useAppToast } from "../../hooks/useAppToast";
import { sanitizeRichText, getCleanDescription } from '../../utils/htmlUtils';
import { Helmet } from 'react-helmet-async';
import CategoryTag from "../../components/common/CategoryTag";
import SectionTitle from "../../components/common/SectionTitle";
import { getPriceLabel } from "../../components/helpers/TicketHelpers";

// Ustawienie domyślnej ikony na marker z Leafleta (bo domyślna ścieżka nie działa bez konfiguracji Webpacka)
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const EventDetailPage = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation("default");
    const location = useLocation();
    const { id } = useParams();
    const [events, setEvents] = useState([]);
    const { favorites, toggleFavorite } = useFavorites(events, setEvents);
    const [similarEvents, setSimilarEvents] = useState([]);
    const [isSimilarSection, setIsSimilarSection] = useState(true);
    const [sectionLoading, setSectionLoading] = useState(true);
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(!event);
    const [viewTab, setViewTab] = useState(0);
    const [showTickets, setShowTickets] = useState(false);
    const [showFreeTickets, setShowFreeTickets] = useState(false);

    const [position, setPosition] = useState(null);
    const toast = useAppToast();
    const baseURL = BASE_URL;
    const myTicketRef = useRef(null);
    const purchaseBoxRef = useRef(null);
    const [qty, setQty] = useState(0);

    useEffect(() => {
        async function geocodeAddress() {
            try {
                const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(event.address)}`);
                const data = await response.json();
                if (data.length > 0) {
                    setPosition({
                        lat: parseFloat(data[0].lat),
                        lng: parseFloat(data[0].lon)
                    });
                } else {
                    setPosition(null);
                }
            } catch (error) {
                console.error(error);
                setPosition(null);
            }
        }

        if (event && event.address) {
            geocodeAddress();
        }
    }, [event]);

    const {
        isOpen: isRegisterOpen,
        onOpen: onRegisterOpen,
        onClose: onRegisterClose,
    } = useDisclosure();

    const {
        isOpen: isSuccessOpen,
        onOpen: onSuccessOpen,
        onClose: onSuccessClose,
    } = useDisclosure();

    const {
        isOpen: isOverlapOpen,
        onOpen: onOverlapOpen,
        onClose: onOverlapClose,
    } = useDisclosure();

    const {
        isOpen: isImageModalOpen,
        onOpen: onImageModalOpen,
        onClose: onImageModalClose,
    } = useDisclosure();

    const fetchSimilarOrLatestEvents = async (loadedEvent) => {
        setSectionLoading(true);
        try {
            const categoryNames = loadedEvent.categories ?? [];
            const hashtagNames = (loadedEvent.hashtags ?? []).map(h =>
                typeof h === 'string' ? h : h.name ?? h.tag ?? ''
            ).filter(Boolean);

            const hasFilters = categoryNames.length > 0 || hashtagNames.length > 0;
            const now = new Date();

            if (hasFilters) {
                const res = await EventsService.getSimilarEvents(
                    loadedEvent.id, categoryNames, hashtagNames
                );
                const items = res?.data?.data?.items ?? [];
                const filtered = items
                    .filter(e => e.id !== loadedEvent.id)
                    .filter(e => new Date(`${e.endDate}T${e.endTime}`) > now)
                    .slice(0, 4);

                if (filtered.length > 0) {
                    setSimilarEvents(filtered);
                    setIsSimilarSection(true);
                    return;
                }
            }

            // Fallback: ostatnio dodane
            const fallback = await EventsService.getAllEvents({
                page: 1,
                pageSize: 5,
                sort: 'created_desc',
            });
            const fallbackItems = (fallback?.data?.data?.items ?? [])
                .filter(e => e.id !== loadedEvent.id)
                .filter(e => new Date(`${e.endDate}T${e.endTime}`) > now)
                .slice(0, 4);

            setSimilarEvents(fallbackItems);
            setIsSimilarSection(false);
        } catch {
            setSimilarEvents([]);
            setIsSimilarSection(false);
        } finally {
            setSectionLoading(false);
        }
    };

    const fetchEvent = async () => {
        window.scrollTo(0, 0);
        setLoading(true);
        try {
            const res = await EventsService.getEvent(id);
            const loadedEvent = res.data.data;
            if (loadedEvent) {
                setEvent(loadedEvent);
                return loadedEvent;
            }
        } catch (error) {
            toast({ title: t('request.error'), status: 'error', duration: 3000, isClosable: true });
        } finally {
            setLoading(false);
        }
        return null;
    };

    useEffect(() => {
        fetchEvent().then((loadedEvent) => {
            if (loadedEvent) fetchSimilarOrLatestEvents(loadedEvent);
        });
    }, [id]);

    if (loading) {
        return (
            <Flex justify="center" align="center" minH="50vh">
                <Spinner size="xl" />
            </Flex>
        );
    }

    if (!event) return <Text>{t('event.empty')}</Text>;

    const handleNavigate = (route, options = {}) => {
        navigate(route, options);
    };

    const handleEvents = () => {
        handleNavigate("/");
    };

    const formattedDate = (date) => {
        const formattedDate = new Intl.DateTimeFormat('pl-PL', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        }).format(date);
        return formattedDate;
    }

    const eventDate = (event) => {
        if (event.startDate === event.endDate) {
            return `${formattedDate(new Date(event.startDate))}, ${event.startTime}–${event.endTime}`;
        }
        return `${formattedDate(new Date(event.startDate))}, ${event.startTime} – ${formattedDate(new Date(event.endDate))}, ${event.endTime}`;
    }

    const getImg = (event) => {
        const path = event?.coverImage;
        return path ? `${baseURL.replace(/\/$/, '')}/${path.replace(/^\//, '')}` : null;
    }

    const openMyCart = (cart) => {
        if (event?.isFree) {
            localStorage.setItem("cart", JSON.stringify({ quantity: qty }));
        } else {
            localStorage.setItem("cart", JSON.stringify(cart));
        }
        localStorage.setItem("event", JSON.stringify(event));
        handleNavigate("/order", { state: event });
    }

    const handleBuyClick = (fromHeader = false) => {
        const proceed = () => {
            if (event.isFree) {
                setShowFreeTickets(true);
            } else {
                setShowTickets(true);
                setTimeout(() => {
                    myTicketRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            }
        };

        if (fromHeader) {
            purchaseBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setTimeout(proceed, 300);
        } else {
            proceed();
        }
    }


    return (
        <>
            <Helmet>
                <title>{event.title} - Evenado</title>
                <meta property="og:title" content={`${event.title} - Evenado`} />
                <meta property="og:description" content={getCleanDescription(event.description) || t('event.default_description', 'Evenado - odkrywaj najlepsze wydarzenia')} />
                {getImg(event) && <meta property="og:image" content={getImg(event)} />}
                <meta property="og:url" content={window.location.href} />
                <meta property="og:type" content="website" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${event.title} - Evenado`} />
                <meta name="twitter:description" content={getCleanDescription(event.description) || t('event.default_description', 'Evenado - odkrywaj najlepsze wydarzenia')} />
                {getImg(event) && <meta name="twitter:image" content={getImg(event)} />}
            </Helmet>
            <Flex flexDirection={'column'} w={"100%"} alignItems={"center"} justifyContent="center" bg={'surface.page'}>
                <EventDetailHeaderPage event={event} onFavoriteToggle={toggleFavorite} onImageClick={onImageModalOpen} onBuyClick={() => handleBuyClick(true)} />
                <Flex w={"100%"} alignItems={"center"} justifyContent="center" px={{ base: '16px', md: '24px', '2xl': 0 }}>
                    <Flex flexDirection={{ base: "column", md: "row" }} gap={{ base: '16px', md: '40px' }} w="100%" maxW="7xl" alignItems={"flex-start"} pt={{ base: '20px', md: '48px' }} pb={{ base: '40px', md: '72px' }} justifyContent={"space-between"}>

                        <Flex flexDirection={'column'} gap={'16px'} w="100%" flex="1" minW={0}>
                            <Flex flexDirection={'column'} gap={{ base: '16px', md: '32px' }} w="100%" flex="1">

                                <Flex role="tablist" display="grid" gridTemplateColumns="repeat(3, minmax(0, 1fr))" gap={{ base: '4px', md: '6px' }} p={{ base: '4px', md: '6px' }}
                                      bg="surface.card" border="1px solid" borderColor="surface.border" borderRadius={{ base: '14px', md: '16px' }}>
                                    {[t('event.tab.about'), t('event.tab.location'), t('event.tab.host')].map((label, idx) => (
                                        <Button key={idx} role="tab" aria-selected={viewTab === idx} onClick={() => setViewTab(idx)}
                                                h={{ base: '44px', md: '46px' }} borderRadius={{ base: '10px', md: '11px' }} px={2}
                                                bg={viewTab === idx ? 'brand.500' : 'transparent'} color={viewTab === idx ? 'white' : 'ink.600'}
                                                _hover={{ bg: viewTab === idx ? 'brand.500' : 'surface.page', boxShadow: 'none' }}
                                                fontSize={{ base: '13px', md: '15px' }} fontWeight={600} whiteSpace="nowrap">
                                            {label}
                                        </Button>
                                    ))}
                                </Flex>
                                {(viewTab === 0) &&
                                    <VStack align="start" spacing={4} flex="1" bg="surface.card" border="1px solid" borderColor="surface.border" borderRadius={{ base: '18px', md: '22px' }} px={{ base: '20px', md: '44px' }} py={{ base: '24px', md: '40px' }}>
                                        <Flex flexDirection={'column'} gap={'16px'} w={"full"}>
                                            <Flex justifyContent={"space-between"} justify={"space-between"} alignItems={{ base: 'flex-start', md: 'center' }} gap={{ base: '12px', md: '16px' }} flexDirection={{ base: 'column', md: 'row' }}>
                                                <Heading as="h2" fontWeight={'700'} fontSize={{ base: "24px", md: "30px" }} letterSpacing="-0.5px">
                                                    {t('event.tab.about')}
                                                </Heading>
                                                <Flex gap={{ base: '4px', md: '16px' }}
                                                    flexWrap="wrap"
                                                    justify={{ base: 'flex-start', md: 'flex-end' }}
                                                    w={{ base: '100%', md: 'auto' }}>
                                                    {event.hashtags && event.hashtags.map((hashtag) => (
                                                        <Button
                                                            key={typeof hashtag === 'string' ? hashtag : hashtag.id} bg={'surface.card'} border={'1px solid'} borderColor={'brand.200'} px={'14px'} h={'36px'} borderRadius={'full'}
                                                            _hover={{ bg: 'brand.100' }} w={'auto'}>
                                                            <Text fontWeight={'600'} fontSize='13px' color='brand.700'>
                                                                #{(typeof hashtag === 'string' ? hashtag : hashtag.name).replace(/^#+/, '')}
                                                            </Text>
                                                        </Button>
                                                    ))}
                                                </Flex>
                                            </Flex>
                                            <Divider borderColor="surface.border" mt="2px" mb="10px" />
                                            <Box
                                                sx={{
                                                    color: "#3B3033",
                                                    fontFamily: "'DM Sans', sans-serif",
                                                    fontWeight: "400",
                                                    fontSize: { base: '15px', md: '17px' },
                                                    lineHeight: { base: 1.65, md: 1.7 },
                                                    "& p": { marginBottom: "1rem" },
                                                    "& strong, & b": { fontWeight: "700", color: "#1E1517" },
                                                    "& ul": { paddingLeft: "1.5rem", marginBottom: "1rem" },
                                                    "& ol": { paddingLeft: "1.5rem", marginBottom: "1rem" },
                                                    "& h1, & h2, & h3, & h4, & h5, & h6": { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: "700", marginY: "1rem", color: "#1E1517" },
                                                    "& a": { color: "#7A1F35", textDecoration: "underline", cursor: "pointer" },
                                                    "& img": { maxWidth: "100%", height: "auto", marginY: "1rem", borderRadius: "8px" },
                                                    "& blockquote": { borderLeft: "4px solid #ccc", paddingLeft: "1rem", marginY: "1rem", fontStyle: "italic", color: "#666" }
                                                }}
                                                dangerouslySetInnerHTML={{ __html: sanitizeRichText(event.description) }}
                                            />
                                        </Flex>
                                    </VStack>
                                }
                                {/* lokalizacja */}
                                {(viewTab === 1) &&
                                    <VStack align="start" spacing={4} flex="1">
                                        <Flex flexDirection={'column'} gap={'16px'} w={"full"}>
                                            <Flex flexDirection={'column'} gap={'8px'} justifyContent={"space-between"} justify={"space-between"}>
                                                {event?.isOnline ? (
                                                    <Box bg="surface.card" border="1px solid" borderColor="surface.border" p={{ base: '20px', md: '32px' }} borderRadius={{ base: '18px', md: '22px' }} w="100%" fontWeight={'400'} color="ink.700">
                                                        <Text fontFamily="heading" fontWeight={'700'} fontSize={{ base: '22px', md: '28px' }} lineHeight={'1.2'} mb="12px" color="ink.900">
                                                            {t('event.online.title', 'Wydarzenie online')}
                                                        </Text>
                                                        {event.platform && (
                                                            <Text mb="16px" fontSize={{ base: '14px', md: '16px' }}>{t('event.online.platform', 'Platforma')}: {event.platform}</Text>
                                                        )}
                                                        {event.url && (
                                                            event.isUserRegistered ? (
                                                                <Button as="a" href={event.url} target="_blank" rel="noopener noreferrer" bgColor="#7A1F35" color="white" _hover={{ bgColor: '#5C1628' }} h="50px" px="32px">
                                                                    {t('event.online.join', 'Dołącz do wydarzenia')}
                                                                </Button>
                                                            ) : (
                                                                <Text color="#7A1F35" fontWeight="700">
                                                                    {t('event.online.signup_required', 'Zapisz się do wydarzenia aby uzyskać dostęp do platformy')}
                                                                </Text>
                                                            )
                                                        )}
                                                    </Box>
                                                ) : position ? (
                                                    <>
                                                    <Box w="100%" h={{ base: '300px', md: '400px' }} borderRadius={{ base: '18px', md: '22px' }} overflow="hidden" border="1px solid" borderColor="surface.border">
                                                        <MapContainer center={position} zoom={15} style={{ height: "100%", width: "100%", cursor: 'pointer' }}>
                                                            <TileLayer
                                                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                                                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                                                            />
                                                            <Marker position={position}>
                                                                <Popup>
                                                                    <Box>
                                                                        {event.address}
                                                                        <br />
                                                                        <a
                                                                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address)}`}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            style={{ color: '#7A1F35', fontWeight: 'bold' }}
                                                                        >
                                                                            {t('map.google')}
                                                                        </a>
                                                                    </Box>
                                                                </Popup>
                                                            </Marker>
                                                            <MapClickHandler address={event.address} />
                                                        </MapContainer>
                                                    </Box>
                                                    <Text fontFamily="heading" fontWeight="700" fontSize={{ base: '18px', md: '22px' }} color="ink.900" lineHeight="130%" mt="8px">
                                                        {event.address}
                                                    </Text>
                                                    </>
                                                ) : (
                                                    <Box
                                                        w="100%"
                                                        h="400px"
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        bg="surface.card"
                                                        border="1px solid"
                                                        borderColor="surface.border"
                                                        borderRadius="22px"
                                                        fontFamily="body"
                                                        fontWeight="500"
                                                        fontSize="18px"
                                                        color="#1E1517"
                                                        cursor="pointer"
                                                        onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.address)}`, '_blank')}
                                                    >
                                                        {event.address}
                                                    </Box>
                                                )}
                                                {!event?.isOnline && event.directions && (
                                                    <Text fontFamily="body" fontWeight="400" fontSize={{ base: '13px', md: '14px' }} color="#4B3F42" lineHeight="150%">
                                                        {event.directions}
                                                    </Text>
                                                )}
                                            </Flex>
                                        </Flex>
                                    </VStack>
                                }
                                {/* organizator */}
                                {(viewTab === 2) &&
                                    <VStack align="start" spacing={4} flex="1">
                                        <Flex flexDirection={'column'} gap={'16px'} w={"full"}>
                                            <Flex justifyContent={"space-between"} justify={"space-between"} gap={'16px'}>
                                                <Box bg="surface.card" border="1px solid" borderColor="surface.border" p={{ base: '20px', md: '32px' }} borderRadius={{ base: '18px', md: '22px' }} w="100%" fontWeight={'400'} color="ink.700">
                                                    <Text fontFamily="heading" fontWeight={'700'} fontSize={{ base: '22px', md: '28px' }} lineHeight={'1.2'} mb="12px" color="ink.900">
                                                        {event.host.name || t('host.empty')}
                                                    </Text>

                                                    {event.host.email && (
                                                        <Text>
                                                            {t('email')}: <a href={`mailto:${event.host.email}`} style={{ color: '#7A1F35' }}>{event.host.email}</a>
                                                        </Text>
                                                    )}

                                                    {event.host.phone && (
                                                        <Text mt="4px">
                                                            {t('phone')}: <a href={`tel:${event.host.phone}`} style={{ color: '#7A1F35' }}>{event.host.phone}</a>
                                                        </Text>
                                                    )}

                                                    {event.host.info && (
                                                        <>
                                                            <Divider borderColor="surface.border" my="16px" />
                                                            <Box
                                                                sx={{
                                                                    color: "#1E1517",
                                                                    fontFamily: "'DM Sans', sans-serif",
                                                                    fontWeight: "300",
                                                                    fontSize: { base: '14px', md: '16px' },
                                                                    lineHeight: '150%',
                                                                    "& p": { marginBottom: "1rem" },
                                                                    "& strong, & b": { fontWeight: "700", color: "#1E1517" },
                                                                    "& ul": { paddingLeft: "1.5rem", marginBottom: "1rem" },
                                                                    "& ol": { paddingLeft: "1.5rem", marginBottom: "1rem" },
                                                                    "& h1, & h2, & h3, & h4, & h5, & h6": { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: "700", marginY: "1rem", color: "#1E1517" },
                                                                    "& a": { color: "#7A1F35", textDecoration: "underline", cursor: "pointer" },
                                                                    "& img": { maxWidth: "100%", height: "auto", marginY: "1rem", borderRadius: "8px" },
                                                                    "& blockquote": { borderLeft: "4px solid #ccc", paddingLeft: "1rem", marginY: "1rem", fontStyle: "italic", color: "#666" }
                                                                }}
                                                                dangerouslySetInnerHTML={{ __html: sanitizeRichText(event.host.info) }}
                                                            />
                                                        </>
                                                    )}
                                                </Box>
                                            </Flex>
                                        </Flex>
                                    </VStack>
                                }
                            </Flex>
                        </Flex>
                        <Flex ref={purchaseBoxRef} flexDirection={'column'} justify={{ base: "center", md: "flex-end" }} align={{ base: "center", md: "flex-end" }} gap={'16px'} w={{ base: "100%", md: "416px" }} flexShrink={0}
                            position={"sticky"} top={'80px'} alignSelf={"flex-start"} scrollMarginTop={'88px'}>
                            <Flex flexDirection={'column'} bg="surface.card" border="1px solid" borderColor="surface.border" borderRadius="22px" boxShadow="raised" p="16px" pb="22px" gap="18px" w="100%">
                                <Flex flexDirection={'column'} gap="16px" w="100%">
                                    {event.coverImage ? (
                                        <Image src={getImg(event)} alt={event.title} objectFit="contain" bg="#F8ECEF"
                                            w="100%" aspectRatio="16 / 9" h="auto" borderRadius={'14px'}
                                            cursor="pointer" onClick={onImageModalOpen} />
                                    ) : (
                                        <Box w="100%" aspectRatio="16 / 9" borderRadius="14px" overflow="hidden"><NoCover size="100%" h="100%" minH={0} /></Box>
                                    )}
                                    <Flex flexDirection={'column'} color="ink.600" px="6px" gap="14px">
                                        <Flex gap={'6px'} flexWrap="wrap">
                                            {event.categories.map((category, index) => (
                                                <CategoryTag key={index} size="sm" fontSize="12px" px="11px" py="4px">{category}</CategoryTag>
                                            ))}
                                        </Flex>
                                        <Heading as="p" fontWeight={'700'} fontSize={{ base: '22px', md: '26px' }} lineHeight={'1.1'} letterSpacing="-0.4px"
                                            wordBreak="break-word">
                                            {event.title}
                                        </Heading>
                                        <Flex flexDirection={'column'} gap="12px" fontSize="15px" lineHeight="1.45">
                                            {event?.showFreeSlotsInfo && <Flex gap="12px">
                                                <svg width="20" height="17" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M14.5 15.5H19V13.75C19 13.2045 18.8251 12.6725 18.4997 12.228C18.1744 11.7836 17.7146 11.4487 17.1845 11.27C16.6543 11.0913 16.0801 11.0776 15.5415 11.2309C15.003 11.3841 14.527 11.6967 14.1796 12.1251M14.5 15.5H5.5M14.5 15.5V13.75C14.5 13.176 14.3866 12.6274 14.1796 12.1251M14.1796 12.1251C13.8454 11.3131 13.2686 10.617 12.5236 10.1267C11.7786 9.63643 10.8996 9.37438 10 9.37438C9.10038 9.37438 8.22138 9.63643 7.4764 10.1267C6.73142 10.617 6.15462 11.3131 5.8204 12.1251M5.5 15.5H1V13.75C1.00004 13.2045 1.1749 12.6725 1.50028 12.228C1.82565 11.7836 2.28537 11.4487 2.81553 11.27C3.34568 11.0913 3.91994 11.0776 4.45846 11.2309C4.99699 11.3841 5.47303 11.6967 5.8204 12.1251M5.5 15.5V13.75C5.5 13.176 5.6134 12.6274 5.8204 12.1251M12.7 4.125C12.7 4.82119 12.4155 5.48887 11.9092 5.98116C11.4028 6.47344 10.7161 6.75 10 6.75C9.28392 6.75 8.59716 6.47344 8.09081 5.98116C7.58446 5.48887 7.3 4.82119 7.3 4.125C7.3 3.42881 7.58446 2.76113 8.09081 2.26884C8.59716 1.77656 9.28392 1.5 10 1.5C10.7161 1.5 11.4028 1.77656 11.9092 2.26884C12.4155 2.76113 12.7 3.42881 12.7 4.125ZM18.1 6.75C18.1 7.21413 17.9104 7.65925 17.5728 7.98744C17.2352 8.31563 16.7774 8.5 16.3 8.5C15.8226 8.5 15.3648 8.31563 15.0272 7.98744C14.6896 7.65925 14.5 7.21413 14.5 6.75C14.5 6.28587 14.6896 5.84075 15.0272 5.51256C15.3648 5.18437 15.8226 5 16.3 5C16.7774 5 17.2352 5.18437 17.5728 5.51256C17.9104 5.84075 18.1 6.28587 18.1 6.75ZM5.5 6.75C5.5 7.21413 5.31036 7.65925 4.97279 7.98744C4.63523 8.31563 4.17739 8.5 3.7 8.5C3.22261 8.5 2.76477 8.31563 2.42721 7.98744C2.08964 7.65925 1.9 7.21413 1.9 6.75C1.9 6.28587 2.08964 5.84075 2.42721 5.51256C2.76477 5.18437 3.22261 5 3.7 5C4.17739 5 4.63523 5.18437 4.97279 5.51256C5.31036 5.84075 5.5 6.28587 5.5 6.75Z" stroke="#7A1F35" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                                {t('seats_available.label')} {event?.maxParticipants - event?.participantsCount}
                                            </Flex>}
                                            <Flex gap="10px" alignItems="flex-start">
                                                {event?.isOnline ? (
                                                    <>
                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7A1F35" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
                                                        <Text>
                                                            {event.platform}
                                                        </Text>
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg style={{ flexShrink: 0, marginTop: '1px' }} width="18" height="21" viewBox="0 0 18 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M14.657 14.9139L10.414 19.1569C10.2284 19.3427 10.0081 19.49 9.76556 19.5905C9.52303 19.6911 9.26305 19.7428 9.0005 19.7428C8.73796 19.7428 8.47798 19.6911 8.23544 19.5905C7.99291 19.49 7.77256 19.3427 7.587 19.1569L3.343 14.9139C2.22422 13.7951 1.46234 12.3696 1.15369 10.8178C0.845043 9.26594 1.00349 7.65742 1.60901 6.19564C2.21452 4.73385 3.2399 3.48445 4.55548 2.60542C5.87107 1.72638 7.41777 1.2572 9 1.2572C10.5822 1.2572 12.1289 1.72638 13.4445 2.60542C14.7601 3.48445 15.7855 4.73385 16.391 6.19564C16.9965 7.65742 17.155 9.26594 16.8463 10.8178C16.5377 12.3696 15.7758 13.7951 14.657 14.9139Z" stroke="#7A1F35" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                                            <path d="M12 9.2569C12 10.0525 11.6839 10.8156 11.1213 11.3782C10.5587 11.9408 9.79565 12.2569 9 12.2569C8.20435 12.2569 7.44129 11.9408 6.87868 11.3782C6.31607 10.8156 6 10.0525 6 9.2569C6 8.46125 6.31607 7.69819 6.87868 7.13558C7.44129 6.57297 8.20435 6.2569 9 6.2569C9.79565 6.2569 10.5587 6.57297 11.1213 7.13558C11.6839 7.69819 12 8.46125 12 9.2569Z" stroke="#7A1F35" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                                        </svg>
                                                        <Text>{event.address}</Text>
                                                    </>
                                                )}
                                            </Flex>
                                            <Flex gap="10px" alignItems="flex-start">
                                                <svg style={{ flexShrink: 0, marginTop: '1px' }} width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M10 6.5V10.5L13 13.5M19 10.5C19 11.6819 18.7672 12.8522 18.3149 13.9442C17.8626 15.0361 17.1997 16.0282 16.364 16.864C15.5282 17.6997 14.5361 18.3626 13.4442 18.8149C12.3522 19.2672 11.1819 19.5 10 19.5C8.8181 19.5 7.64778 19.2672 6.55585 18.8149C5.46392 18.3626 4.47177 17.6997 3.63604 16.864C2.80031 16.0282 2.13738 15.0361 1.68508 13.9442C1.23279 12.8522 1 11.6819 1 10.5C1 8.11305 1.94821 5.82387 3.63604 4.13604C5.32387 2.44821 7.61305 1.5 10 1.5C12.3869 1.5 14.6761 2.44821 16.364 4.13604C18.0518 5.82387 19 8.11305 19 10.5Z" stroke="#7A1F35" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                                </svg>
                                                <Text>{eventDate(event)}</Text>
                                            </Flex>
                                            <Flex gap="10px" alignItems="flex-start">
                                                <Box flexShrink={0} mt="1px"><svg width="18" height="18" viewBox="0 0 21 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M13.5 1.5V3.5M13.5 7.5V9.5M13.5 13.5V15.5M3.5 1.5C2.96957 1.5 2.46086 1.71071 2.08579 2.08579C1.71071 2.46086 1.5 2.96957 1.5 3.5V6.5C2.03043 6.5 2.53914 6.71071 2.91421 7.08579C3.28929 7.46086 3.5 7.96957 3.5 8.5C3.5 9.03043 3.28929 9.53914 2.91421 9.91421C2.53914 10.2893 2.03043 10.5 1.5 10.5V13.5C1.5 14.0304 1.71071 14.5391 2.08579 14.9142C2.46086 15.2893 2.96957 15.5 3.5 15.5H17.5C18.0304 15.5 18.5391 15.2893 18.9142 14.9142C19.2893 14.5391 19.5 14.0304 19.5 13.5V10.5C18.9696 10.5 18.4609 10.2893 18.0858 9.91421C17.7107 9.53914 17.5 9.03043 17.5 8.5C17.5 7.96957 17.7107 7.46086 18.0858 7.08579C18.4609 6.71071 18.9696 6.5 19.5 6.5V3.5C19.5 2.96957 19.2893 2.46086 18.9142 2.08579C18.5391 1.71071 18.0304 1.5 17.5 1.5H3.5Z" stroke="#7A1F35" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg></Box>
                                                <Text>{getPriceLabel(event, t, i18n.language)}</Text>
                                            </Flex>
                                        </Flex>
                                    </Flex>
                                </Flex>
                                {isCancelled(event) && (
                                    <Alert status="error" borderRadius={'12px'}>
                                        <AlertIcon />
                                        {t('event.cancelled.message', 'To wydarzenie zostało odwołane przez organizatora. Rejestracja i zakup biletów są niedostępne.')}
                                    </Alert>
                                )}
                                {!showFreeTickets && !isCancelled(event) && (
                                    <Box px="6px" pt="6px"><BuyTicketButton event={event} onClick={() => handleBuyClick(false)} /></Box>
                                )}
                                {showFreeTickets && !isCancelled(event) && <Box>
                                    <FreeTicketsQuantity
                                        available={event?.maxParticipants - event?.participantsCount}
                                        maxPerOrder={event?.maxParticipants - event?.participantsCount}
                                        value={qty}
                                        onChange={setQty}
                                        onReserve={(cart) => openMyCart(cart)}
                                        label="Wybierz ilość biletów"
                                    />
                                </Box>
                                }
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>

                {showTickets &&
                    <Flex w={"100%"} alignItems={"center"} justifyContent="center" className="ticket-section" mb={'80px'} ref={myTicketRef} scrollMarginTop={'72px'}>
                        <Flex flexDirection={{ base: 'column', md: 'row' }} gap={'32px'} w="100%" maxW="7xl" px={{ base: '16px', md: '24px', '2xl': 0 }}
                            alignItems={"flex-start"} py={'48px'} justifyContent={"space-between"}>
                            <VStack align="center" spacing={4} flex="1" gap="32px" w={"100%"}>
                                <Divider borderColor={'#DED3D4'} w={{ base: '90%', md: '100%' }} alignSelf={"center"} />
                                <Flex flexDirection={'column'} gap={'32px'} w={"100%"}>
                                    <Text fontSize={{ base: "20px", md: "28px" }} textAlign={"center"}>
                                        {t('event.tickets')}
                                    </Text>
                                    <TicketsCart
                                        tickets={event.ticketTypes}
                                        maxParticipants={event.maxParticipants}
                                        participantsCount={event.participantsCount}
                                        onBuy={(cart) => openMyCart(cart)}
                                    />
                                </Flex>
                            </VStack>
                        </Flex>
                    </Flex>
                }

                <Flex bg="surface.muted" w={"full"}>
                    <Flex w={"100%"} alignItems={"center"} justifyContent="center">
                        <Flex flexDirection={'column'} w="100%" maxW="7xl" px={{ base: '16px', md: '24px', '2xl': 0 }} alignItems={"flex-start"} py={{ base: '36px', md: '56px' }} justifyContent={"space-between"}>
                            <SectionTitle borderColor="#E6D5DA">
                                {isSimilarSection ? t('event.similar') : t('event.other', 'Inne wydarzenia')}
                            </SectionTitle>

                            {sectionLoading ? (
                                <Flex justify="center" w="100%" py="32px">
                                    <Spinner size="md" color="#7A1F35" />
                                </Flex>
                            ) : (
                                <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={{ base: '16px', md: '24px' }} w="100%">
                                    {similarEvents.length === 0 ? (
                                        <Text fontSize={{ base: '15px', md: '17px' }} lineHeight="1.6" color="ink.500" gridColumn="1 / -1">
                                            {t('event.not_found')}
                                        </Text>
                                    ) : (
                                        similarEvents.map((ev) => (
                                            <Event key={ev.id} event={ev} />
                                        ))
                                    )}
                                </SimpleGrid>
                            )}
                        </Flex>
                    </Flex>
                </Flex>

                {/* Modale */}
                <RegistrationModal
                    isOpen={isRegisterOpen}
                    onClose={onRegisterClose}
                    onSuccess={() => {
                        onRegisterClose();
                        onSuccessOpen();
                    }}
                    onOverlap={onOverlapOpen}
                    event={event}
                />

                <SuccessModal
                    isOpen={isSuccessOpen}
                    onClose={onSuccessClose}
                    eventTitle={event.title}
                />

                <OverlapModal
                    isOpen={isOverlapOpen}
                    onClose={onOverlapClose}
                    eventTitle={event.title}
                />

                <ImageModal
                    isOpen={isImageModalOpen}
                    onClose={onImageModalClose}
                    src={getImg(event)}
                    alt={event.title}
                />
            </Flex>
        </>
    );
};

export default EventDetailPage;
